import NextImage from 'next/image';
import {
  BriefcaseIcon,
  NoSymbolIcon,
  SparklesIcon,
} from '@heroicons/react/20/solid';
import {
  Cog8ToothIcon,
  DocumentMagnifyingGlassIcon,
  HomeIcon,
  MinusIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon, EyeIcon } from '@heroicons/react/24/solid';
import {
  AlertTriangle,
  ArchiveIcon,
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  CircleArrowRightIcon,
  CircleCheck,
  CirclePlusIcon,
  CircleX,
  ClipboardCheck,
  ClipboardListIcon,
  Clock1,
  Clock4,
  Copy,
  CornerDownLeftIcon,
  CreditCard,
  DollarSign,
  Download,
  Ellipsis,
  Expand,
  ExternalLink,
  File,
  FileEdit,
  FileSearchIcon,
  FileText,
  FlagIcon,
  Flame,
  Footprints,
  Gauge,
  GoalIcon,
  GripVertical,
  HeartHandshake,
  HelpCircle,
  Image,
  Inbox,
  Info,
  Laptop,
  LayoutGrid,
  LinkIcon,
  Loader2,
  Lock,
  type LucideProps,
  Mail,
  MailWarning,
  MapPinIcon,
  MegaphoneIcon,
  Menu,
  MonitorPlay,
  Moon,
  MoreHorizontal,
  MoreVertical,
  PenSquare,
  Pencil,
  Phone,
  PictureInPicture,
  Pizza,
  Plus,
  RefreshCcw,
  RefreshCw,
  RocketIcon,
  Search,
  SearchCheck,
  SendIcon,
  ShieldCheck,
  ShieldPlus,
  SquareArrowOutUpRightIcon,
  Store,
  SunMedium,
  ThumbsDown,
  ThumbsUp,
  Trash,
  Trash2Icon,
  TriangleAlertIcon,
  Trophy,
  Upload,
  UploadCloud,
  UserCheck,
  UserIcon,
  Video,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import duplicateApplicantIcon from '../images/duplicate-applicant-icon.svg';
import hireGoIcon from '../images/hire-go-icon.svg';

export const Icons = {
  triangleAlertIcon: TriangleAlertIcon,
  fileSearchIcon: FileSearchIcon,
  squareArrowOutUpRightIcon: SquareArrowOutUpRightIcon,
  duplicateApplicant: ({ className }: { className?: string }) => (
    <div className={cn('h-4 w-4', className)}>
      <NextImage
        src={duplicateApplicantIcon}
        alt="duplicate applicant"
        width={32}
        height={32}
      />
    </div>
  ),
  hireGo: ({ className }: { className?: string }) => (
    <div className={cn('flex h-4 w-4 items-center justify-center', className)}>
      <NextImage
        src={hireGoIcon}
        alt="hire go"
        width={38}
        height={38}
        className="block"
      />
    </div>
  ),
  minus: MinusIcon,
  flag: FlagIcon,
  circleArrowRightIcon: CircleArrowRightIcon,
  chevronDown: ChevronDown,
  copy: Copy,
  pencil: Pencil,
  pictureInPicture: PictureInPicture,
  download: Download,
  upload: Upload,
  uploadCloud: UploadCloud,
  refreshCcw: RefreshCcw,
  refreshCw: RefreshCw,
  goal: GoalIcon,
  archive: ArchiveIcon,
  rocket: RocketIcon,
  clipboardList: ClipboardListIcon,
  linkIcon: LinkIcon,
  megaPhone: MegaphoneIcon,
  cornerDownLeft: CornerDownLeftIcon,
  fountainLogo: ({ ...props }) => (
    <svg
      width="126"
      height="20"
      viewBox="0 0 126 20"
      fill={props.fill}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M4.65937 19.5165C2.60226 19.5165 0.931641 17.8418 0.931641 15.7797C0.931641 13.7177 2.60226 12.043 4.65937 12.043C7.2775 12.043 9.2141 13.9718 10.0951 17.2753H11.1548C12.0359 13.9718 13.9725 12.0389 16.5906 12.0389C18.6477 12.0389 20.3183 13.7135 20.3183 15.7756C20.3183 17.8377 18.6477 19.5123 16.5906 19.5123H4.65937V19.5165ZM20.3183 6.06094C20.3183 8.12301 18.6477 9.79766 16.5906 9.79766C14.1013 9.79766 12.0525 10.9683 10.6271 13.047C9.20163 10.9683 7.14867 9.79766 4.66352 9.79766C2.60642 9.79766 0.935797 8.12301 0.935797 6.06094C0.935797 3.99887 2.60642 2.32422 4.66352 2.32422C7.28166 2.32422 9.21825 4.25299 10.0993 7.55646H11.1548C12.0359 4.25299 13.9725 2.32422 16.5906 2.32422C18.6519 2.32422 20.3183 3.9947 20.3183 6.06094Z"
        fill="currentFill"
      />
      <path
        d="M35.221 0.187461C35.7073 0.312435 36.1021 0.470735 36.4054 0.666528V3.47011C36.1395 3.31181 35.8319 3.18684 35.487 3.09519C35.1421 3.00771 34.7639 2.96188 34.3566 2.96188C33.646 2.96188 33.1224 3.13685 32.7858 3.48261C32.4491 3.82837 32.2788 4.34076 32.2788 5.01562V6.05707H36.1935V9.07311H32.2788V19.5126H28.63V9.07728H26.2861V6.06124H28.63V5.1281C28.63 3.5076 29.0497 2.24953 29.8933 1.34972C30.737 0.449907 31.9671 0 33.5837 0C34.1863 0 34.7307 0.062487 35.221 0.187461ZM49.8951 9.10227C50.4894 10.1895 50.7886 11.4268 50.7886 12.814C50.7886 14.2012 50.4894 15.4343 49.8951 16.5132C49.3008 17.588 48.4696 18.4253 47.4058 19.021C46.3419 19.6167 45.1242 19.9167 43.757 19.9167C42.3898 19.9167 41.2303 19.6167 40.1747 19.021C39.1192 18.4253 38.2922 17.588 37.6979 16.5132C37.1036 15.4385 36.8044 14.2054 36.8044 12.814C36.8044 11.4226 37.0995 10.1895 37.6854 9.10227C38.2714 8.015 39.0942 7.1735 40.1498 6.5653C41.2054 5.96126 42.4105 5.65715 43.757 5.65715C45.1035 5.65715 46.3419 5.96126 47.4058 6.5653C48.4696 7.1735 49.3008 8.01916 49.8951 9.10227ZM47.0608 12.814C47.0608 12.0308 46.932 11.3351 46.6744 10.7311C46.4167 10.1271 46.0385 9.65632 45.544 9.31473C45.0453 8.9773 44.451 8.8065 43.7612 8.8065C43.0713 8.8065 42.5061 8.9773 42.0157 9.31473C41.5254 9.65216 41.1555 10.1187 40.8978 10.7144C40.6402 11.3101 40.5113 12.01 40.5113 12.8098C40.5113 13.6097 40.6443 14.2804 40.9103 14.8802C41.1763 15.4801 41.5503 15.9383 42.0282 16.2674C42.5061 16.5965 43.0838 16.759 43.7612 16.759C44.4385 16.759 45.0494 16.5965 45.544 16.2674C46.0385 15.9383 46.4167 15.4759 46.6744 14.8802C46.932 14.2887 47.0608 13.5972 47.0608 12.814ZM60.9868 14.0179C60.9868 14.8719 60.7624 15.5384 60.3094 16.0217C59.8565 16.5049 59.2414 16.7423 58.4601 16.7423C57.6788 16.7423 57.0929 16.4966 56.6482 16.0092C56.2035 15.5218 55.9833 14.8552 55.9833 14.0179V6.06124H52.3595V14.2554C52.3595 15.376 52.6046 16.3716 53.0909 17.2464C53.5771 18.1171 54.2794 18.7961 55.1937 19.2752C56.108 19.7542 57.1968 19.9958 58.456 19.9958C59.7152 19.9958 60.8414 19.7542 61.7723 19.2752C62.7032 18.7961 63.4138 18.1171 63.9042 17.2464C64.3904 16.3758 64.6356 15.376 64.6356 14.2554V6.06124H60.9868V14.0179ZM77.2068 6.31535C76.5128 5.87794 75.6775 5.66132 74.705 5.66132C73.4957 5.66132 72.4983 5.96542 71.7087 6.56947C71.1435 7.00271 70.7072 7.54843 70.3913 8.19829V6.06124H66.7966V19.5168H70.4204V12.2808C70.4204 11.1935 70.6739 10.3353 71.1809 9.70631C71.6879 9.07311 72.3944 8.75651 73.2962 8.75651C74.198 8.75651 74.7965 9.01479 75.1871 9.53135C75.5778 10.0479 75.7731 10.8394 75.7731 11.9059V19.5126H79.3969V11.081C79.3969 9.96043 79.2057 8.99396 78.8234 8.18579C78.4369 7.37346 77.9008 6.75276 77.2068 6.31535ZM103.559 8.4774C104.045 9.37721 104.29 10.4478 104.29 11.6934V19.5168H100.72V17.8504C100.446 18.3587 100.072 18.7878 99.5898 19.1293C98.8542 19.6542 97.969 19.9167 96.9384 19.9167C96.0699 19.9167 95.2927 19.7292 94.607 19.3543C93.9255 18.9794 93.3894 18.4628 93.0112 17.8046C92.6289 17.1464 92.4377 16.4091 92.4377 15.5884C92.4377 14.6969 92.6413 13.9304 93.0486 13.2806C93.4559 12.6307 94.0211 12.1266 94.74 11.7725C95.459 11.4185 96.2901 11.2393 97.2293 11.2393C98.1685 11.2393 98.9955 11.4351 99.6521 11.8267C100.084 12.085 100.438 12.3974 100.716 12.764V12.0683C100.716 11.0019 100.413 10.177 99.8101 9.598C99.2075 9.01896 98.3638 8.73151 97.2792 8.73151C96.4813 8.73151 95.7249 8.87315 95.0143 9.15643C94.3037 9.4397 93.6844 9.84378 93.1483 10.3562V7.15268C93.805 6.70694 94.5405 6.34868 95.3592 6.06957C96.1738 5.79463 97.0714 5.65715 98.048 5.65715C99.3446 5.65715 100.463 5.90294 101.406 6.39034C102.358 6.88607 103.072 7.57759 103.559 8.4774ZM100.458 16.4049C100.654 16.0925 100.753 15.7426 100.753 15.351C100.753 14.7636 100.529 14.2887 100.088 13.9221C99.6438 13.5555 99.0579 13.3764 98.3306 13.3764C97.5659 13.3764 96.9675 13.5555 96.5311 13.9096C96.0948 14.2637 95.8787 14.7553 95.8787 15.376C95.8787 15.9967 96.0906 16.4966 96.5187 16.8715C96.9467 17.2464 97.541 17.4339 98.3015 17.4339C98.7961 17.4339 99.2324 17.3422 99.6064 17.1548C99.9804 16.9673 100.263 16.7174 100.458 16.4049ZM124.491 8.18163C124.109 7.37347 123.573 6.74859 122.879 6.31119C122.185 5.87378 121.349 5.65715 120.377 5.65715C119.168 5.65715 118.17 5.96126 117.381 6.5653C116.815 6.99854 116.379 7.54426 116.063 8.19413V6.05707H112.469V19.5126H116.092V12.2808C116.092 11.1935 116.346 10.3353 116.853 9.70631C117.36 9.07311 118.066 8.75651 118.968 8.75651C119.87 8.75651 120.468 9.01479 120.859 9.53135C121.25 10.0479 121.445 10.8394 121.445 11.9059V19.5126H125.069V11.081C125.065 9.96043 124.874 8.99396 124.491 8.18163ZM90.044 16.7798C89.7406 16.8965 89.3624 16.9548 88.897 16.9548C88.257 16.9548 87.7708 16.7798 87.4342 16.4341C87.0975 16.0883 86.9272 15.5676 86.9272 14.8719V9.07728H90.7879V6.06124H86.9272V2.32452H83.2784V6.06124H80.5605V9.07728H83.2784V15.326C83.2784 16.2716 83.4529 17.0881 83.7979 17.7838C84.1428 18.4795 84.6581 19.021 85.3438 19.4126C86.0254 19.8042 86.8565 20 87.8331 20C88.5438 20 89.1297 19.9292 89.591 19.7875C90.0523 19.6459 90.5053 19.4335 90.95 19.146V16.3174L90.925 16.3424C90.6383 16.5174 90.3474 16.6632 90.044 16.7798ZM106.555 19.5168H110.204V6.06124H106.555V19.5168ZM108.354 0.133306C107.149 0.133306 106.168 1.11227 106.168 2.32035C106.168 3.52843 107.145 4.50739 108.354 4.50739C109.564 4.50739 110.54 3.52843 110.54 2.32035C110.54 1.11227 109.56 0.133306 108.354 0.133306Z"
        fill="currentFill"
      />
    </svg>
  ),
  info: Info,
  noSymbol: NoSymbolIcon,
  briefcase: BriefcaseIcon,
  documentMagnifyingGlass: DocumentMagnifyingGlassIcon,
  building: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M0.87868 5.12106C2.05025 6.29263 3.94975 6.29263 5.12132 5.12106C5.26529 4.97709 5.39156 4.82213 5.50015 4.65889C6.03763 5.46711 6.95661 5.99977 8 5.99977C9.04353 5.99977 9.96262 5.46697 10.5001 4.65856C10.6087 4.82194 10.7351 4.97702 10.8791 5.12109C12.0507 6.29267 13.9502 6.29267 15.1218 5.12109C16.2933 3.94952 16.2933 2.05003 15.1218 0.878453L14.8291 0.585786C14.454 0.210714 13.9453 0 13.4149 0H2.58552C2.05509 0 1.54638 0.210713 1.17131 0.585786L0.87868 0.878418C-0.292893 2.04999 -0.292893 3.94949 0.87868 5.12106Z"
        fill="currentColor"
      />
      <path
        d="M1 7.03223C2.42799 7.74067 4.15393 7.64395 5.50057 6.74205C6.21499 7.22007 7.07471 7.49977 8 7.49977C8.92537 7.49977 9.78517 7.22002 10.4996 6.74191C11.8462 7.64388 13.572 7.74073 15 7.03249V14.5H15.25C15.6642 14.5 16 14.8358 16 15.25C16 15.6642 15.6642 16 15.25 16H10.75C10.3358 16 10 15.6642 10 15.25V11.75C10 11.3358 9.66421 11 9.25 11H6.75C6.33579 11 6 11.3358 6 11.75V15.25C6 15.6642 5.66421 16 5.25 16H0.75C0.335786 16 0 15.6642 0 15.25C0 14.8358 0.335786 14.5 0.75 14.5H1V7.03223Z"
        fill="currentColor"
      />
    </svg>
  ),

  truck: TruckIcon,
  close: X,
  closeSolid: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM6.28033 5.21967C5.98744 4.92678 5.51256 4.92678 5.21967 5.21967C4.92678 5.51256 4.92678 5.98744 5.21967 6.28033L6.93934 8L5.21967 9.71967C4.92678 10.0126 4.92678 10.4874 5.21967 10.7803C5.51256 11.0732 5.98744 11.0732 6.28033 10.7803L8 9.06066L9.71967 10.7803C10.0126 11.0732 10.4874 11.0732 10.7803 10.7803C11.0732 10.4874 11.0732 10.0126 10.7803 9.71967L9.06066 8L10.7803 6.28033C11.0732 5.98744 11.0732 5.51256 10.7803 5.21967C10.4874 4.92678 10.0126 4.92678 9.71967 5.21967L8 6.93934L6.28033 5.21967Z"
        fill="currentColor"
      />
    </svg>
  ),
  spinner: Loader2,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  chevronFirst: ChevronFirst,
  chevronLast: ChevronLast,
  trash: Trash,
  trash2: Trash2Icon,
  post: FileText,
  page: File,
  x: X,
  graphUp: ({ ...props }) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.577 4.87834C12.6842 4.47824 13.0955 4.2408 13.4956 4.34801L18.2766 5.6291C18.4688 5.68058 18.6326 5.80628 18.732 5.97854C18.8315 6.1508 18.8585 6.35552 18.807 6.54766L17.5259 11.3287C17.4187 11.7288 17.0074 11.9663 16.6073 11.8591C16.2072 11.7519 15.9698 11.3406 16.077 10.9405L16.8865 7.9195C14.6303 9.30965 12.7541 11.0901 11.2935 13.1222C11.1651 13.3009 10.9646 13.4142 10.7452 13.432C10.5259 13.4499 10.3098 13.3704 10.1542 13.2148L7 10.0607L2.28033 14.7803C1.98744 15.0732 1.51256 15.0732 1.21967 14.7803C0.926777 14.4874 0.926777 14.0126 1.21967 13.7197L6.46967 8.46968C6.76256 8.17679 7.23744 8.17679 7.53033 8.46968L10.6039 11.5433C12.1049 9.63051 13.9633 7.9506 16.1492 6.61197L13.1073 5.7969C12.7072 5.68969 12.4698 5.27844 12.577 4.87834Z"
        fill="#1ECA44"
      />
    </svg>
  ),
  send: ({ ...props }) => (
    <svg
      width="13"
      height="12"
      viewBox="0 0 13 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11.8033 0.0371658C12.1776 -0.112568 12.5688 0.21277 12.4897 0.608112L10.2929 11.5914C10.2197 11.9577 9.78925 12.1221 9.49041 11.898L5.95046 9.24323L4.3737 11.0171C4.06826 11.3607 3.5 11.1447 3.5 10.6849V7.45L5.80103 5.11056C5.95423 4.9548 5.78242 4.70132 5.58099 4.78592L2.00805 6.28661L0.703182 5.30803C0.386305 5.07039 0.451486 4.57759 0.819252 4.4305L11.8033 0.0371658Z"
        fill="white"
      />
    </svg>
  ),
  edit: ({ ...props }) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1.69485 13.7628L0.433058 16.9173C0.269796 17.3254 0.674836 17.7304 1.08299 17.5672L4.23746 16.3054C4.74042 16.1042 5.19728 15.803 5.58033 15.4199L16.4995 4.5012C17.3279 3.67278 17.3279 2.32963 16.4995 1.5012C15.6711 0.672777 14.3279 0.672775 13.4995 1.5012L2.58033 12.4199C2.19728 12.803 1.89603 13.2598 1.69485 13.7628Z"
        fill="currentColor"
      />
    </svg>
  ),
  mailWarning: MailWarning,
  media: Image,
  menu: Menu,
  clock1: Clock1,
  clock4: Clock4,
  store: Store,
  table: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M2 6.00001H14M2 8.66668H14M8 6.00001V11.3333M3.33333 11.3333H12.6667C13.403 11.3333 14 10.7364 14 10V4.66668C14 3.9303 13.403 3.33334 12.6667 3.33334H3.33333C2.59695 3.33334 2 3.9303 2 4.66668V10C2 10.7364 2.59695 11.3333 3.33333 11.3333Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  inbox: Inbox,
  billing: CreditCard,
  ellipsis: Ellipsis,
  moreVertical: MoreVertical,
  expand: Expand,
  add: Plus,
  warning: AlertTriangle,
  warningFilled: ExclamationTriangleIcon,
  userIcon: UserIcon,
  user: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M10.6666 4.66667C10.6666 6.13942 9.47274 7.33333 7.99998 7.33333C6.52722 7.33333 5.33331 6.13942 5.33331 4.66667C5.33331 3.19391 6.52722 2 7.99998 2C9.47274 2 10.6666 3.19391 10.6666 4.66667Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.99998 9.33333C5.42265 9.33333 3.33331 11.4227 3.33331 14H12.6666C12.6666 11.4227 10.5773 9.33333 7.99998 9.33333Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  userCheck: UserCheck,
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  help: HelpCircle,
  pizza: Pizza,
  sun: SunMedium,
  moon: Moon,
  laptop: Laptop,
  gauge: Gauge,
  penSquare: PenSquare,
  search: Search,
  searchCheck: SearchCheck,
  briefcaseBusiness: BriefcaseBusiness,
  upDown: ({ ...props }) => (
    <svg
      width="8"
      height="16"
      viewBox="0 0 8 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M4 15L1 10L7 10L4 15Z" fill="currentColor" />
      <path d="M4 1L7 6H1L4 1Z" fill="currentColor" />
    </svg>
  ),
  searchSolid: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M5.5 7.25C5.5 6.2835 6.2835 5.5 7.25 5.5C8.2165 5.5 9 6.2835 9 7.25C9 7.73339 8.80467 8.1702 8.48744 8.48744C8.1702 8.80467 7.73339 9 7.25 9C6.2835 9 5.5 8.2165 5.5 7.25Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 1.5C4.41015 1.5 1.5 4.41015 1.5 8C1.5 11.5899 4.41015 14.5 8 14.5C11.5899 14.5 14.5 11.5899 14.5 8C14.5 4.41015 11.5899 1.5 8 1.5ZM7.25 4.5C5.73122 4.5 4.5 5.73122 4.5 7.25C4.5 8.76878 5.73122 10 7.25 10C7.82853 10 8.3658 9.82097 8.80871 9.51582L10.1464 10.8536C10.3417 11.0488 10.6583 11.0488 10.8536 10.8536C11.0488 10.6583 11.0488 10.3417 10.8536 10.1464L9.51582 8.80871C9.82097 8.3658 10 7.82853 10 7.25C10 5.73122 8.76878 4.5 7.25 4.5Z"
        fill="currentColor"
      />
    </svg>
  ),
  layoutGrid: LayoutGrid,
  phone: Phone,
  email: Mail,
  checkCircle: CheckCircle,
  calendar: Calendar,
  video: Video,
  flame: Flame,
  dollar: DollarSign,
  compensation: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11.3333 5.99999V4.66666C11.3333 3.93028 10.7364 3.33333 10 3.33333H3.33333C2.59695 3.33333 2 3.93028 2 4.66666V8.66666C2 9.40304 2.59695 9.99999 3.33333 9.99999H4.66667M6 12.6667H12.6667C13.403 12.6667 14 12.0697 14 11.3333V7.33333C14 6.59695 13.403 5.99999 12.6667 5.99999H6C5.26362 5.99999 4.66667 6.59695 4.66667 7.33333V11.3333C4.66667 12.0697 5.26362 12.6667 6 12.6667ZM10.6667 9.33333C10.6667 10.0697 10.0697 10.6667 9.33333 10.6667C8.59695 10.6667 8 10.0697 8 9.33333C8 8.59695 8.59695 7.99999 9.33333 7.99999C10.0697 7.99999 10.6667 8.59695 10.6667 9.33333Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  fileEdit: FileEdit,
  sparkles: SparklesIcon,
  footPrints: Footprints,
  check: Check,
  trophy: Trophy,
  heartHandshake: HeartHandshake,
  briepcase: BriefcaseIcon,
  threeDot: MoreHorizontal,
  mapPin: MapPinIcon,
  users: ({ ...props }) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M12 4.35418C12.7329 3.52375 13.8053 3 15 3C17.2091 3 19 4.79086 19 7C19 9.20914 17.2091 11 15 11C13.8053 11 12.7329 10.4762 12 9.64582M15 21H3V20C3 16.6863 5.68629 14 9 14C12.3137 14 15 16.6863 15 20V21ZM15 21H21V20C21 16.6863 18.3137 14 15 14C13.9071 14 12.8825 14.2922 12 14.8027M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  gear: Cog8ToothIcon,
  shieldCheck: ShieldCheck,
  eye: EyeIcon,
  quote: ({ ...props }: LucideProps) => (
    <svg
      width="18"
      height="22"
      viewBox="0 0 18 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 0.5C7.07904 0.5 5.18374 0.611142 3.32022 0.827409C1.82283 1.00119 0.75 2.28722 0.75 3.75699V20.75C0.75 20.9989 0.873449 21.2315 1.07953 21.3711C1.28561 21.5106 1.54748 21.5388 1.77854 21.4464L5.25 20.0578L8.72146 21.4464C8.90027 21.5179 9.09973 21.5179 9.27854 21.4464L12.75 20.0578L16.2215 21.4464C16.4525 21.5388 16.7144 21.5106 16.9205 21.3711C17.1266 21.2315 17.25 20.9989 17.25 20.75V3.75699C17.25 2.28722 16.1772 1.00119 14.6798 0.82741C12.8163 0.611142 10.921 0.5 9 0.5ZM12.5303 7.78033C12.8232 7.48744 12.8232 7.01256 12.5303 6.71967C12.2374 6.42678 11.7626 6.42678 11.4697 6.71967L5.46967 12.7197C5.17678 13.0126 5.17678 13.4874 5.46967 13.7803C5.76256 14.0732 6.23744 14.0732 6.53033 13.7803L12.5303 7.78033ZM5.625 8C5.625 7.37868 6.12868 6.875 6.75 6.875C7.37132 6.875 7.875 7.37868 7.875 8C7.875 8.62132 7.37132 9.125 6.75 9.125C6.12868 9.125 5.625 8.62132 5.625 8ZM11.25 11.375C10.6287 11.375 10.125 11.8787 10.125 12.5C10.125 13.1213 10.6287 13.625 11.25 13.625C11.8713 13.625 12.375 13.1213 12.375 12.5C12.375 11.8787 11.8713 11.375 11.25 11.375Z"
        fill="currentColor"
      />
    </svg>
  ),
  overview: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M6.00065 3.33333H4.66732C3.93094 3.33333 3.33398 3.93029 3.33398 4.66667V12.6667C3.33398 13.403 3.93094 14 4.66732 14H11.334C12.0704 14 12.6673 13.403 12.6673 12.6667V4.66667C12.6673 3.93029 12.0704 3.33333 11.334 3.33333H10.0007M6.00065 3.33333C6.00065 4.06971 6.5976 4.66667 7.33398 4.66667H8.66732C9.4037 4.66667 10.0007 4.06971 10.0007 3.33333M6.00065 3.33333C6.00065 2.59695 6.5976 2 7.33398 2H8.66732C9.4037 2 10.0007 2.59695 10.0007 3.33333M8.00065 8H10.0007M8.00065 10.6667H10.0007M6.00065 8H6.00732M6.00065 10.6667H6.00732"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  applicants: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8 2.90278C8.48863 2.34917 9.20354 2 10 2C11.4728 2 12.6667 3.19391 12.6667 4.66667C12.6667 6.13942 11.4728 7.33333 10 7.33333C9.20354 7.33333 8.48863 6.98416 8 6.43055M10 14H2V13.3333C2 11.1242 3.79086 9.33333 6 9.33333C8.20914 9.33333 10 11.1242 10 13.3333V14ZM10 14H14V13.3333C14 11.1242 12.2091 9.33333 10 9.33333C9.27143 9.33333 8.58835 9.52812 8 9.86846M8.66667 4.66667C8.66667 6.13942 7.47276 7.33333 6 7.33333C4.52724 7.33333 3.33333 6.13942 3.33333 4.66667C3.33333 3.19391 4.52724 2 6 2C7.47276 2 8.66667 3.19391 8.66667 4.66667Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  interviews: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M5.33333 4.66667V2M10.6667 4.66667V2M4.66667 7.33333H11.3333M3.33333 14H12.6667C13.403 14 14 13.403 14 12.6667V4.66667C14 3.93029 13.403 3.33333 12.6667 3.33333H3.33333C2.59695 3.33333 2 3.93029 2 4.66667V12.6667C2 13.403 2.59695 14 3.33333 14Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  settings: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M6.88309 2.87821C7.16735 1.70726 8.83265 1.70726 9.11692 2.87821C9.30055 3.63462 10.1672 3.99358 10.8319 3.58857C11.8609 2.96159 13.0384 4.13913 12.4114 5.16812C12.0064 5.83284 12.3654 6.69945 13.1218 6.88308C14.2927 7.16735 14.2927 8.83265 13.1218 9.11691C12.3654 9.30055 12.0064 10.1672 12.4114 10.8319C13.0384 11.8609 11.8609 13.0384 10.8319 12.4114C10.1672 12.0064 9.30055 12.3654 9.11692 13.1218C8.83265 14.2927 7.16735 14.2927 6.88309 13.1218C6.69945 12.3654 5.83284 12.0064 5.16812 12.4114C4.13914 13.0384 2.96159 11.8609 3.58857 10.8319C3.99358 10.1672 3.63462 9.30055 2.87821 9.11691C1.70726 8.83265 1.70726 7.16735 2.87821 6.88308C3.63462 6.69945 3.99358 5.83284 3.58857 5.16812C2.96159 4.13913 4.13914 2.96159 5.16812 3.58857C5.83284 3.99358 6.69945 3.63462 6.88309 2.87821Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6C9.10457 6 10 6.89543 10 8Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  clipboardCheck: ClipboardCheck,
  thumbsUp: ThumbsUp,
  thumbsDown: ThumbsDown,
  target: ({ ...props }) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8.75 15.25L20.25 3.75M11.2502 9.16734C10.552 8.89779 9.79328 8.75 9 8.75C5.54822 8.75 2.75 11.5482 2.75 15C2.75 18.4518 5.54822 21.25 9 21.25C12.4518 21.25 15.25 18.4518 15.25 15C15.25 14.2067 15.1022 13.448 14.8327 12.7498M14.25 9.75L17.25 6.75M14.25 9.75V5.5L17.25 2.75V6.75M14.25 9.75H18.25L21.25 6.75H17.25M12.25 15C12.25 16.7949 10.7949 18.25 9 18.25C7.20507 18.25 5.75 16.7949 5.75 15C5.75 13.2051 7.20507 11.75 9 11.75C10.7949 11.75 12.25 13.2051 12.25 15Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  documentSearch: ({ ...props }) => (
    <svg
      width="16"
      height="20"
      viewBox="0 0 16 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M6 19H13C14.1046 19 15 18.1046 15 17V7.41421C15 7.149 14.8946 6.89464 14.7071 6.70711L9.29289 1.29289C9.10536 1.10536 8.851 1 8.58579 1H3C1.89543 1 1 1.89543 1 3V14M1 19L5.87868 14.1213M5.87868 14.1213C6.42157 14.6642 7.17157 15 8 15C9.65685 15 11 13.6569 11 12C11 10.3431 9.65685 9 8 9C6.34315 9 5 10.3431 5 12C5 12.8284 5.33579 13.5784 5.87868 14.1213Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  filter: ({ ...props }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  dotsHorizontal: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M3.33268 7.99967H3.33935M7.99935 7.99967H8.00602M12.666 7.99967H12.6727M3.99935 7.99967C3.99935 8.36786 3.70087 8.66634 3.33268 8.66634C2.96449 8.66634 2.66602 8.36786 2.66602 7.99967C2.66602 7.63148 2.96449 7.33301 3.33268 7.33301C3.70087 7.33301 3.99935 7.63148 3.99935 7.99967ZM8.66602 7.99967C8.66602 8.36786 8.36754 8.66634 7.99935 8.66634C7.63116 8.66634 7.33268 8.36786 7.33268 7.99967C7.33268 7.63148 7.63116 7.33301 7.99935 7.33301C8.36754 7.33301 8.66602 7.63148 8.66602 7.99967ZM13.3327 7.99967C13.3327 8.36786 13.0342 8.66634 12.666 8.66634C12.2978 8.66634 11.9993 8.36786 11.9993 7.99967C11.9993 7.63148 12.2978 7.33301 12.666 7.33301C13.0342 7.33301 13.3327 7.63148 13.3327 7.99967Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  dotsVertical: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8.00008 3.33335L8.00008 3.34002M8.00008 8.00002L8.00008 8.00669M8.00008 12.6667L8.00008 12.6734M8.00008 4.00002C7.63189 4.00002 7.33341 3.70154 7.33341 3.33335C7.33341 2.96516 7.63189 2.66669 8.00008 2.66669C8.36827 2.66669 8.66675 2.96516 8.66675 3.33335C8.66675 3.70154 8.36827 4.00002 8.00008 4.00002ZM8.00008 8.66669C7.63189 8.66669 7.33341 8.36821 7.33341 8.00002C7.33341 7.63183 7.63189 7.33336 8.00008 7.33336C8.36827 7.33336 8.66675 7.63183 8.66675 8.00002C8.66675 8.36821 8.36827 8.66669 8.00008 8.66669ZM8.00008 13.3334C7.63189 13.3334 7.33341 13.0349 7.33341 12.6667C7.33341 12.2985 7.63189 12 8.00008 12C8.36827 12 8.66675 12.2985 8.66675 12.6667C8.66675 13.0349 8.36827 13.3334 8.00008 13.3334Z"
        stroke="#4F46E5"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  chevronSmRight: ({ ...props }) => (
    <svg
      width="8"
      height="16"
      viewBox="0 0 8 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M2 6.33618L4.66667 9.00012L2 11.6641"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  workflowDataCollection: ({ ...props }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      {...props}
    >
      <path d="M3 2C2.44772 2 2 2.44772 2 3V6C2 6.55228 2.44772 7 3 7H6C6.55228 7 7 6.55228 7 6V3C7 2.44772 6.55228 2 6 2H3Z" />
      <path d="M3 9C2.44772 9 2 9.44772 2 10V13C2 13.5523 2.44772 14 3 14H6C6.55228 14 7 13.5523 7 13V10C7 9.44772 6.55228 9 6 9H3Z" />
      <path d="M9 10C9 9.44772 9.44772 9 10 9H13C13.5523 9 14 9.44772 14 10V13C14 13.5523 13.5523 14 13 14H10C9.44772 14 9 13.5523 9 13V10Z" />
      <path d="M13.842 4.09347C14.083 3.81062 14.0439 3.39024 13.7547 3.15454C13.4654 2.91883 13.0354 2.95704 12.7944 3.23989L10.9996 5.34573L10.1639 4.52861C9.89767 4.26826 9.46597 4.26826 9.1997 4.52861C8.93343 4.78895 8.93343 5.21106 9.1997 5.47141L10.5633 6.80474C10.6988 6.93718 10.885 7.0078 11.0763 6.99931C11.2677 6.99083 11.4466 6.90402 11.5692 6.76012L13.842 4.09347Z" />
    </svg>
  ),
  workflowInterview: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M11 3V2.75C11 2.33579 10.6642 2 10.25 2C9.83579 2 9.5 2.33579 9.5 2.75V3H6.5V2.75C6.5 2.33579 6.16422 2 5.75 2C5.3358 2 5.00002 2.33577 5.00001 2.74998L5 3H4C3.44772 3 3 3.44771 3 4L3 4.5H13V4C13 3.44772 12.5523 3 12.0001 3C11.6482 3 11.2729 3 11 3Z" />
      <path d="M3 6V12C3 12.5523 3.44772 13 4 13H12C12.5523 13 13 12.552 13 11.9997C13 9.87984 13 8.31758 13 6H3Z" />
    </svg>
  ),
  workflowFindingSpeakerPhone: ({ ...props }) => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M0 4C0 1.79086 1.79086 0 4 0H28C30.2091 0 32 1.79086 32 4V28C32 30.2091 30.2091 32 28 32H4C1.79086 32 0 30.2091 0 28V4Z"
        fill="url(#paint0_linear_6648_4443)"
      />
      <path
        d="M0 4C0 1.79086 1.79086 0 4 0H28C30.2091 0 32 1.79086 32 4V28C32 30.2091 30.2091 32 28 32H4C1.79086 32 0 30.2091 0 28V4Z"
        fill="url(#paint1_radial_6648_4443)"
        fillOpacity="0.64"
      />
      <path
        d="M15.3333 11.9215V20.8268C15.3333 21.4747 14.8081 22 14.1601 22C13.6643 22 13.222 21.6883 13.0553 21.2214L11.6243 17.1219M20 16.6667C21.1046 16.6667 22 15.7712 22 14.6667C22 13.5621 21.1046 12.6667 20 12.6667M11.6243 17.1219C10.6695 16.7161 10 15.7696 10 14.6667C10 13.1939 11.1939 12 12.6667 12H13.8881C16.6218 12 18.9713 11.1773 20 10L20 19.3333C18.9713 18.1561 16.6218 17.3333 13.8881 17.3333L12.6667 17.3333C12.2968 17.3333 11.9445 17.258 11.6243 17.1219Z"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id="paint0_linear_6648_4443"
          x1="16"
          y1="-8.66667"
          x2="16"
          y2="49.3333"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0733013" stopColor="#E095F9" />
          <stop offset="0.9" stopColor="#0A07E3" />
        </linearGradient>
        <radialGradient
          id="paint1_radial_6648_4443"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(-1.24176e-06 28) rotate(-33.4078) scale(37.5352)"
        >
          <stop stopColor="#A10091" />
          <stop offset="0.9" stopColor="#A10091" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  ),
  workflowScreeningDocument: ({ ...props }) => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M0 4C0 1.79086 1.79086 0 4 0H28C30.2091 0 32 1.79086 32 4V28C32 30.2091 30.2091 32 28 32H4C1.79086 32 0 30.2091 0 28V4Z"
        fill="url(#paint0_linear_6648_3879)"
      />
      <path
        d="M0 4C0 1.79086 1.79086 0 4 0H28C30.2091 0 32 1.79086 32 4V28C32 30.2091 30.2091 32 28 32H4C1.79086 32 0 30.2091 0 28V4Z"
        fill="url(#paint1_radial_6648_3879)"
        fillOpacity="0.64"
      />
      <path
        d="M14.6668 21.3333H19.3335C20.0699 21.3333 20.6668 20.7363 20.6668 19.9999V13.6094C20.6668 13.4326 20.5966 13.263 20.4716 13.138L16.8621 9.52851C16.7371 9.40349 16.5675 9.33325 16.3907 9.33325H12.6668C11.9304 9.33325 11.3335 9.93021 11.3335 10.6666V17.9999M11.3335 21.3333L14.5859 18.0808M14.5859 18.0808C14.9479 18.4427 15.4479 18.6666 16.0002 18.6666C17.1047 18.6666 18.0002 17.7712 18.0002 16.6666C18.0002 15.562 17.1047 14.6666 16.0002 14.6666C14.8956 14.6666 14.0002 15.562 14.0002 16.6666C14.0002 17.2189 14.224 17.7189 14.5859 18.0808Z"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <defs>
        <linearGradient
          id="paint0_linear_6648_3879"
          x1="16"
          y1="-8.66667"
          x2="16"
          y2="49.3333"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0733013" stopColor="#E095F9" />
          <stop offset="0.9" stopColor="#0A07E3" />
        </linearGradient>
        <radialGradient
          id="paint1_radial_6648_3879"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(-1.24176e-06 28) rotate(-33.4078) scale(37.5352)"
        >
          <stop stopColor="#A10091" />
          <stop offset="0.9" stopColor="#A10091" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  ),
  workflowHired: ({ ...props }) => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M0 4C0 1.79086 1.79086 0 4 0H28C30.2091 0 32 1.79086 32 4V28C32 30.2091 30.2091 32 28 32H4C1.79086 32 0 30.2091 0 28V4Z"
        fill="#475569"
      />
      <path
        d="M13.7892 16.8946C13.9241 16.6254 14 16.3216 14 16C14 15.6784 13.9241 15.3746 13.7892 15.1054M13.7892 16.8946C13.4609 17.5501 12.783 18 12 18C10.8954 18 10 17.1046 10 16C10 14.8954 10.8954 14 12 14C12.783 14 13.4609 14.4499 13.7892 15.1054M13.7892 16.8946L18.2108 19.1054M13.7892 15.1054L18.2108 12.8946M18.2108 12.8946C18.5391 13.5501 19.217 14 20 14C21.1046 14 22 13.1046 22 12C22 10.8954 21.1046 10 20 10C18.8954 10 18 10.8954 18 12C18 12.3216 18.0759 12.6254 18.2108 12.8946ZM18.2108 19.1054C18.0759 19.3746 18 19.6784 18 20C18 21.1046 18.8954 22 20 22C21.1046 22 22 21.1046 22 20C22 18.8954 21.1046 18 20 18C19.217 18 18.5391 18.4499 18.2108 19.1054Z"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  campaignConfirmation: ({ ...props }) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8.99963 12.0001L10.9996 14.0001L14.9996 10.0001M7.83437 4.69717C8.55191 4.63991 9.23309 4.35775 9.78095 3.89087C11.0594 2.80135 12.9398 2.80135 14.2183 3.89087C14.7662 4.35775 15.4474 4.63991 16.1649 4.69717C17.8393 4.83079 19.169 6.16043 19.3026 7.83486C19.3598 8.55239 19.642 9.23358 20.1089 9.78144C21.1984 11.0599 21.1984 12.9403 20.1089 14.2188C19.642 14.7667 19.3598 15.4479 19.3026 16.1654C19.169 17.8398 17.8393 19.1695 16.1649 19.3031C15.4474 19.3603 14.7662 19.6425 14.2183 20.1094C12.9398 21.1989 11.0594 21.1989 9.78095 20.1094C9.23309 19.6425 8.55191 19.3603 7.83437 19.3031C6.15994 19.1695 4.8303 17.8398 4.69668 16.1654C4.63942 15.4478 4.35727 14.7667 3.89038 14.2188C2.80086 12.9403 2.80086 11.0599 3.89038 9.78144C4.35727 9.23358 4.63942 8.55239 4.69668 7.83486C4.8303 6.16043 6.15994 4.83079 7.83437 4.69717Z"
        stroke="#4EA441"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  ),
  workflowDocumentSigning: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M14 12.748C14 12.3328 13.6633 11.9961 13.248 11.9961H2.75196C2.33666 11.9961 2 12.3328 2 12.748C2 13.1633 2.33666 13.5 2.75196 13.5H13.248C13.6633 13.5 14 13.1633 14 12.748Z" />
      <path d="M9.80803 6.85274C9.90553 6.75585 10.063 6.75585 10.1605 6.85274L10.3097 7.00107C10.4081 7.09883 10.4081 7.25798 10.3097 7.35573L8.77687 8.87901C8.6785 8.97676 8.6785 9.13591 8.77687 9.23367L9.30409 9.75759C9.4016 9.85448 9.55903 9.85448 9.65654 9.75759L11.811 7.61659C12.063 7.3662 12.063 6.99061 11.811 6.74022L11.0446 5.97858C10.9462 5.88083 10.9462 5.72168 11.0446 5.62392L11.1181 5.55086C11.685 4.98748 11.685 3.98592 11.1181 3.42254C10.5512 2.85915 9.54331 2.85915 8.97638 3.42254L4.14756 8.22113C4.05311 8.31498 4 8.44264 4 8.57579V10.2461C4 10.3842 4.11193 10.4961 4.25 10.4961H5.93554C6.06761 10.4961 6.19431 10.4438 6.28799 10.3507L9.80803 6.85274Z" />
    </svg>
  ),
  home: HomeIcon,
  chatSpinner: () => (
    <svg
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      className="size-6 animate-spin stroke-zinc-400"
    >
      <path d="M12 3v3m6.366-.366-2.12 2.12M21 12h-3m.366 6.366-2.12-2.12M12 21v-3m-6.366.366 2.12-2.12M3 12h3m-.366-6.366 2.12 2.12"></path>
    </svg>
  ),
  mail: Mail,
  externalLink: ExternalLink,
  dot: ({ ...props }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="4"
      height="4"
      viewBox="0 0 4 4"
      fill="none"
      {...props}
    >
      <circle cx="2" cy="2" r="2" />
    </svg>
  ),
  messageBubbles: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11.3333 5.33334H12.6667C13.403 5.33334 14 5.93029 14 6.66667V10.6667C14 11.4031 13.403 12 12.6667 12H11.3333V14.6667L8.66667 12H6C5.63181 12 5.29848 11.8508 5.05719 11.6095M5.05719 11.6095L7.33333 9.33334H10C10.7364 9.33334 11.3333 8.73638 11.3333 8.00001V4.00001C11.3333 3.26363 10.7364 2.66667 10 2.66667H3.33333C2.59695 2.66667 2 3.26363 2 4.00001V8.00001C2 8.73638 2.59695 9.33334 3.33333 9.33334H4.66667V12L5.05719 11.6095Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  planeSend: SendIcon,
  circlePlus: CirclePlusIcon,
  progressOneThird: ({ ...props }) => (
    <svg
      width="36"
      height="8"
      viewBox="0 0 36 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4 8C5.86384 8 7.42994 6.72523 7.87398 5H14.126C14.5701 6.72523 16.1362 8 18 8C19.8638 8 21.4299 6.72523 21.874 5H28.126C28.5701 6.72523 30.1362 8 32 8C34.2091 8 36 6.20914 36 4C36 1.79086 34.2091 0 32 0C30.1362 0 28.5701 1.27477 28.126 3H21.874C21.4299 1.27477 19.8638 0 18 0C16.1362 0 14.5701 1.27477 14.126 3H7.87398C7.42994 1.27477 5.86384 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z"
      />
    </svg>
  ),
  circleX: CircleX,
  circleCheck: CircleCheck,
  magnifyingGlassOnDocument: ({ ...props }) => (
    <svg
      width="16"
      height="20"
      viewBox="0 0 16 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        stroke="#4F46E5"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 19H13C14.1046 19 15 18.1046 15 17V7.41421C15 7.149 14.8946 6.89464 14.7071 6.70711L9.29289 1.29289C9.10536 1.10536 8.851 1 8.58579 1H3C1.89543 1 1 1.89543 1 3V14M1 19L5.87868 14.1213M5.87868 14.1213C6.42157 14.6642 7.17157 15 8 15C9.65685 15 11 13.6569 11 12C11 10.3431 9.65685 9 8 9C6.34315 9 5 10.3431 5 12C5 12.8284 5.33579 13.5784 5.87868 14.1213Z"
      />
    </svg>
  ),
  paperAirplane: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M7 11.6667L13 13L7 1L1 13L7 11.6667ZM7 11.6667V6.33333"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  monitorPlay: MonitorPlay,
  shieldPlus: ShieldPlus,
  lock: Lock,
  gripVertical: GripVertical,
  google: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M15.545 6.558a9.42 9.42 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.689 7.689 0 0 1 5.352 2.082l-2.284 2.284A4.347 4.347 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.792 4.792 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.702 3.702 0 0 0 1.599-2.431H8V6.558h7.545z"
        fill="currentColor"
      />
    </svg>
  ),
  viewBoards: ({ ...props }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M6 11.333V4.66634M6 11.333C6 12.0694 5.40305 12.6663 4.66667 12.6663H3.33333C2.59695 12.6663 2 12.0694 2 11.333V4.66634C2 3.92996 2.59695 3.33301 3.33333 3.33301H4.66667C5.40305 3.33301 6 3.92996 6 4.66634M6 11.333C6 12.0694 6.59695 12.6663 7.33333 12.6663H8.66667C9.40305 12.6663 10 12.0694 10 11.333M6 4.66634C6 3.92996 6.59695 3.33301 7.33333 3.33301H8.66667C9.40305 3.33301 10 3.92996 10 4.66634M10 11.333V4.66634M10 11.333C10 12.0694 10.597 12.6663 11.3333 12.6663H12.6667C13.403 12.6663 14 12.0694 14 11.333V4.66634C14 3.92996 13.403 3.33301 12.6667 3.33301H11.3333C10.597 3.33301 10 3.92996 10 4.66634"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};
